import { MenuOutlined } from "@ant-design/icons";
import chevronDown from "@assets/redesign/icons/chevronDown.svg";
import chevronUp from "@assets/redesign/icons/chevronUp.svg";
import origEdit from "@assets/redesign/icons/inputEdit.svg";
import delete_icon from "@assets/redesign/icons/trash.svg";
import {
  IUserEventDetail,
  USER_CUSTOM_EVENT,
} from "@components/Context/SocketContext";
import Loader from "@components/global/Loader/Loader";
import Copy from "@components/global/new/Copy/Copy";
import Icon from "@components/global/new/Icon/Icon";
import { Panel } from "@components/global/new/Panel/Panel";
import useLocationsNav from "@mobx/hooks/useLocationsNav";
import { useWatchNetwork } from "@mobx/services/http";
import mainLocationsStore from "@mobx/store/locations";
import { ILocation3 } from "@mobx/store/locations/types";
import unallocatedMinersStore from "@mobx/store/locations/unallocated";
import {
  ChangeUserColor,
  SocketEventTypes,
} from "@services/http/socket/responses";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { useMe } from "@utils/hooks/useMe";
import { makeNotifs } from "@utils/makeNotifs";
import { Collapse } from "antd";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { RingLoader } from "react-spinners";
import LocationMore3 from "../LocationMore3";
import styles from "./LocationRow.module.scss";
import { WLP202603 } from "../../../config";
import wlpEdit from "@components/WLP202603/assets/redesign/icons/inputEdit.svg";
import { TextFieldWithCopy } from "@components/TextFieldWithCopy/TextFieldWithCopy";
import { useAuth } from "@utils/hooks/useAuth";

const edit = WLP202603 ? wlpEdit : origEdit;

export interface ILocationRow3Props {
  readonly headers: string[];
  readonly isPhone?: boolean;
  readonly location: ILocation3;
  readonly index?: number;
  onDrag(ev: React.PointerEvent): void;
}

export const useControl = (props: ILocationRow3Props) => {

  const locationStore = mainLocationsStore.locations[props.location.Id];
  const { ok } = makeNotifs();
  const { onSuccess } = useGlobalNotifs();
  const auth = useAuth();
  // const me = useMe();
  // const isAdmin = me?.is_admin;
  const [active, setActive] = useState("");
  const [isLoading, setLoading] = useState(false);
  const { setActiveLocation } = useLocationsNav();
  useWatchNetwork(locationStore);

  const hasActive = useMemo(() => mainLocationsStore.activeLocation !== undefined, [mainLocationsStore.activeLocation]);

  useEffect(() => {
    setActive(mainLocationsStore.activeLocation?.location.Id === props.location.Id ? "1" : "");
  }, [mainLocationsStore.activeLocation?.location.Id]);

  useEffect(() => {
    const loading = () => {
      if (locationStore.isLoading) return true;
      for (const b of locationStore.buildingsInOrder) {
        if (b.isLoading) return true;
      }
      return false;
    };
    if (loading()) {
      setLoading(true);
      return;
    }
    setTimeout(() => {
      if (!loading()) setLoading(false);
    }, 400);
  }, [locationStore.isLoading, ...locationStore.buildingsInOrder.map(b => b.isLoading)]);

  useEffect(() => {
    document.addEventListener(USER_CUSTOM_EVENT, onUserEvent);
    function onUserEvent(e: Event) {
      const data = (e as CustomEvent).detail as IUserEventDetail;
      switch (data.type) {
        case SocketEventTypes.CHANGE_USER_COLOR: {
          const ev = data.event as ChangeUserColor;
          unallocatedMinersStore.setMinerColor(ev.user_id, ev.color);
          break;
        }
      }
    }
    return () => {
      document.removeEventListener(USER_CUSTOM_EVENT, onUserEvent);
    };
  }, []);

  const showAddNewLocation = () => mainLocationsStore.showAddLocationModal();
  const editLocation = () => locationStore?.showEditLocationModal();
  const deleteLocation = async (id: number) => {
    const r = await mainLocationsStore.deleteLocation(id);
    if (r) onSuccess(ok.success);
  };

  return {
    isAdmin: auth.isAdmin,
    adminUuid: auth.uuid,
    active,
    hasActive,
    isLoading,

    showAddNewLocation,
    editLocation,
    deleteLocation,
    toggleActive() {
      setActiveLocation(active === "" ? props.location.Id : -1);
    },
  };
};

interface RowItem3Props {
  children: ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  isPhone?: boolean;
  isOdd?: boolean;
  header: string;
}

const RowItem3 = (props: RowItem3Props) =>
  props.isPhone ? (
    <div className={styles.phoneRowItem}>
      <div className={styles.phoneRowItemHeader}>{props.header}</div>
      <div
        className={cn(styles.item, {
          [styles.last_item]: props.isLast,
          [styles.first_item]: props.isFirst,
          [styles.odd_item]: props.isOdd,
        })}
      >
        {props.children}
      </div>
    </div>
  ) : (
    <div
      className={cn(styles.item, {
        [styles.last_item]: props.isLast,
      })}
    >
      {props.children}
    </div>
  );

const LocationRow3 = observer((props: ILocationRow3Props) => {

  const ctrl = useControl(props);

  //useEffect(() => { console.log("LOC", props.location) }, [props.location]);
  //useEffect(() => { dbg.log("COORD", props.location.Coord) }, [props.location]);

  return (
    <Collapse
      className={styles.collapse}
      collapsible="icon"
      ghost
      activeKey={ctrl.active}
      items={[
        {
          key: "1",
          label: <Panel
            className={cn(styles.wrapper, {
              [styles.activePanel]: WLP202603 && !!ctrl.active,
            })}
            theme={ctrl.active ? "orange" : "light"}
            onClick={ctrl.toggleActive}
          >
            {ctrl.isLoading && <div className={styles.loader}><Loader transparent /></div>}
            {typeof props.index === "number" && <span className={styles.index}>{props.index + 1}</span>}
            <MenuOutlined
              className={styles.move_icon}
              style={{ color: "#0a28ab" }}
              onPointerDown={props.onDrag}
              onClick={e => e.stopPropagation()}
              rev={undefined}
            />
            <div className={styles.row_content}>
              <RowItem3
                header={props.headers[0]}
                isPhone={props.isPhone}
                isFirst
                isOdd
              >
                {props.location.Name}
              </RowItem3>
              <RowItem3 header={props.headers[1]} isPhone={props.isPhone}>
                {props.location.Code}
              </RowItem3>
              <RowItem3 header={props.headers[2]} isPhone={props.isPhone} isOdd>
                {props.location.Address}
              </RowItem3>
              {ctrl.isAdmin && (<>
                <RowItem3 header={props.headers[3]} isPhone={props.isPhone} isOdd>
                  <TextFieldWithCopy guid={props.location.Uuid} style={{ maxWidth: 230 }} />
                  {/* <div className={styles.row_uuid} onClick={e => e.stopPropagation()}>
                    <TextFieldWithCopy guid="550e8400-e29b-41d4-a716-446655440000" />
                    <span className={styles.dataWithCopySpan}>
                      {props.location.Uuid}
                      {props.location.Uuid && (
                        <Copy text={props.location.Uuid}>
                          <i></i>
                        </Copy>
                      )}
                    </span>
                  </div> */}
                </RowItem3>
                <RowItem3 header={props.headers[4]} isPhone={props.isPhone}>
                  {props.location.Coord && (
                    <>{props.location.Coord.replace(/,/g, ', ')}</>
                  )}
                </RowItem3>
                <RowItem3
                  header={props.headers[5]}
                  isPhone={props.isPhone}
                  isLast
                  isOdd
                >
                  <span>{props.location.Notes}</span>
                  {ctrl.isAdmin && (
                    <div className={styles.icons}>
                      <Icon
                        src={edit}
                        className={styles.icon}
                        onClick={e => {
                          e.stopPropagation();
                          ctrl.editLocation();
                        }}
                      />
                      <Icon
                        src={delete_icon}
                        className={styles.icon}
                        onClick={e => {
                          e.stopPropagation();
                          ctrl.deleteLocation(props.location.Id);
                        }}
                      />
                      {ctrl.isLoading
                        ? <RingLoader size={16} />
                        : <Icon
                          className={styles.icon}
                          src={ctrl.active ? chevronUp : chevronDown}
                          noHover
                        />
                      }
                    </div>
                  )}
                </RowItem3>
              </>)}
            </div>
          </Panel>,
          children: ctrl.isAdmin && (
            <Panel theme="dark" className={styles.more}>
              <LocationMore3 locationID={props.location.Id} isOpen={!!ctrl.active} />
            </Panel>
          ),
          showArrow: false,
          forceRender: true,
        },
      ]}
    />
  );
});

LocationRow3.displayName = "LocationRow3";

export default LocationRow3;
